import {isUndefined} from 'lodash';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {CrmSyncErrorsBannerContainer} from '../../../../../../../src/components/accounts/crm/banners/crmSyncErrorsBannerContainer';
import {onViewErrorsClick} from '../../../../../../../src/components/accounts/crm/common/crmSyncHelpers';
import {HubspotCrmSyncDialogContainer} from '../../../../../../../src/components/accounts/crm/hubspot/hubspotCrmSyncDialogContainer';
import {SalesforceCrmSyncDialogContainer} from '../../../../../../../src/components/accounts/crm/salesforce/salesforceCrmSyncDialogContainer';
import {
  buildContactsManagerAccountCsvImportUrl,
  buildContactsManagerAccountsUrl,
  buildContactsManagerAccountSyncsUrl,
  buildContactsManagerSyncsUrl,
} from '../../../../../../../src/helpers/contactsManagerHelpers';
import {useAppSelector} from '../../../../../../../src/helpers/reduxHelpers';
import {
  CrmSyncIntegrationResourceTypesEnum,
  CrmSyncModel,
  CrmSyncSourcesEnum,
  CrmSyncStatusesEnum,
} from '../../../../../../../src/models/api/crmSyncModel';
import {SyncJobModel} from '../../../../../../../src/models/api/jobModel';
import {getCompanyHasCrmSyncFeature} from '../../../../../../../src/store/data/company/companySelectors';
import {ContactsManagerPanelId} from '../../../../../../../src/store/panels/panelStateBase';
import {Plural, Trans} from '../../../../../../shared/helpers/src/internationalization/react';
import {Handler, HandlerOf} from '../../../../../../shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../../../../shared/ui/src/components/icon/icon';
import {InfoButton} from '../common/buttons/infoButton';
import {ContactsManagerImportPopover} from '../common/contactsManagerImportPopover';
import {CrmSyncHeaderDetailsContainer} from '../common/crmSyncHeaderDetailsContainer';
import {AccountInfoBoxesContainer} from '../common/infobox/accountInfoBoxesContainer';
import {VerticalRule} from '../common/verticalRule';
import {ContactsManagerHeader} from '../contactsManagerHeader';
import {buildInfoBoxToggleLabel} from '../helpers/infoBoxHelpers';
import {AccountsTableContainer} from '../tables/accounts/accountsTableContainer';

/*
 * Props.
 */

interface AccountsPageProps {
  panelId: ContactsManagerPanelId;
  totalResults: number | undefined;
  isInfoBoxExpanded: boolean;
  hasRunningImports: boolean;
  activeSyncJobs: ReadonlyArray<SyncJobModel>;
  isShowingImportSalesforceDialog: boolean;
  isShowingImportHubspotDialog: boolean;
  crmSyncs: ReadonlyArray<CrmSyncModel>;
  shouldShowCrmSyncErrors?: boolean;
  hasCrmSyncFullPageSalesforce: boolean;
  hasMicrosoftDynamicsAccountSync: boolean;
  hasHubspotContactSync: boolean;
  isCrmSyncConnectedById: {[crmSyncId: number]: boolean | undefined};
  openCreateAccountForm: Handler;
  onToggleInfoBox: Handler;
  navigateToLink: HandlerOf<string>;
}

/*
 * Styles.
 */

const StyledButtonContentDiv = styled.div`
  display: flex;
  align-items: center;
`;

const StyledAccountIcon = styled(Icon)`
  margin-right: 8px;
`;

const StyledHeaderCount = styled.div`
  margin-left: 4px;
`;

const StyledWrapperDiv = styled.div`
  display: flex;
  height: inherit;
`;

const StyledContentWrapperDiv = styled.div`
  flex: 1;
  display: flex;
  flex-flow: column;
`;

const StyledContextDetailsWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  flex-wrap: wrap;
`;

const StyledCrmSyncErrorsWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  gap: 8px;
  padding: 16px 48px 16px 45px;

  &:empty {
    padding: 0;
  }
`;

const StyledCrmSyncErrorsBannerContainer = styled(CrmSyncErrorsBannerContainer)`
  border-radius: 18px;
`;

/*
 * Component.
 */

export const AccountsPage: FC<AccountsPageProps> = (props) => {
  const {
    panelId,
    totalResults,
    isInfoBoxExpanded,
    activeSyncJobs,
    isShowingImportSalesforceDialog,
    isShowingImportHubspotDialog,
    crmSyncs,
    shouldShowCrmSyncErrors,
    hasCrmSyncFullPageSalesforce,
    hasMicrosoftDynamicsAccountSync,
    hasHubspotContactSync,
    isCrmSyncConnectedById,
    navigateToLink,
    openCreateAccountForm,
    onToggleInfoBox,
  } = props;
  const [hiddenCrmSyncErrorBannerIds, setHiddenCrmSyncErrorBannerIds] = useState<ReadonlyArray<number>>([]);
  const onImportCSVClick = () => {
    navigateToLink(buildContactsManagerAccountCsvImportUrl());
  };
  const onImportSalesforceClick = () => {
    // if they have the full page feature, direct them to the new syncs page
    if (hasCrmSyncFullPageSalesforce) {
      navigateToLink(buildContactsManagerSyncsUrl(CrmSyncSourcesEnum.SALESFORCE));
    } else {
      navigateToLink(buildContactsManagerAccountSyncsUrl(CrmSyncSourcesEnum.SALESFORCE));
    }
  };

  const onImportHubspotClick = () => {
    if (hasHubspotContactSync) {
      navigateToLink(buildContactsManagerSyncsUrl(CrmSyncSourcesEnum.HUBSPOT));
    } else {
      navigateToLink(buildContactsManagerAccountSyncsUrl(CrmSyncSourcesEnum.HUBSPOT));
    }
  };

  const onImportMicrosoftDynamicsClick = () => {
    navigateToLink(buildContactsManagerSyncsUrl(CrmSyncSourcesEnum.MICROSOFT_DYNAMICS));
  };

  const onHideCrmSyncErrorBanner = (syncId: number) => {
    setHiddenCrmSyncErrorBannerIds((syncIds) => [...syncIds, syncId]);
  };

  const hasCrmSyncFeature = useAppSelector(getCompanyHasCrmSyncFeature);

  const shouldShowErrorBannerForSource = (crmSync?: CrmSyncModel) => {
    if (!crmSync || crmSync.status === CrmSyncStatusesEnum.DISABLED || !isCrmSyncConnectedById[crmSync.id]) {
      return false;
    }
    switch (crmSync.source) {
      case CrmSyncSourcesEnum.SALESFORCE:
      case CrmSyncSourcesEnum.HUBSPOT:
        return true;
      case CrmSyncSourcesEnum.MICROSOFT_DYNAMICS:
        return hasMicrosoftDynamicsAccountSync;
      default:
        return false;
    }
  };

  return (
    <>
      <ContactsManagerHeader
        title={<Trans id="contacts-manager-accounts-title">Accounts</Trans>}
        interactionId="contacts-manager-accounts-header"
        contextDetails={maybeRenderContextDetails(panelId, totalResults, activeSyncJobs)}
        actions={
          <>
            {renderCreateAccountButton(openCreateAccountForm)}
            {renderImportButton(
              onImportCSVClick,
              onImportSalesforceClick,
              onImportHubspotClick,
              onImportMicrosoftDynamicsClick,
              hasCrmSyncFeature,
              hasMicrosoftDynamicsAccountSync,
              ButtonVariantsEnum.PRIMARY,
            )}
            <VerticalRule />
            <InfoButton
              label={buildInfoBoxToggleLabel()}
              onToggle={onToggleInfoBox}
              isActive={isInfoBoxExpanded}
            />
          </>
        }
      />
      <StyledWrapperDiv>
        <StyledContentWrapperDiv>
          <StyledCrmSyncErrorsWrapperDiv>
            {crmSyncs.map((crmSync) => {
              if (!shouldShowErrorBannerForSource(crmSync)) {
                return null;
              }

              if (hiddenCrmSyncErrorBannerIds.includes(crmSync.id)) {
                return null;
              }
              return (
                <StyledCrmSyncErrorsBannerContainer
                  key={crmSync.id}
                  syncId={crmSync.id}
                  onViewErrorsClick={() =>
                    onViewErrorsClick(
                      crmSync.source,
                      hasCrmSyncFullPageSalesforce,
                      hasHubspotContactSync,
                      navigateToLink,
                    )
                  }
                  onCloseClick={() => onHideCrmSyncErrorBanner(crmSync.id)}
                  shouldDisplayCrmSyncName
                />
              );
            })}
          </StyledCrmSyncErrorsWrapperDiv>
          <AccountsTableContainer
            panelId={panelId}
            renderImportButton={() =>
              renderImportButton(
                onImportCSVClick,
                onImportSalesforceClick,
                onImportHubspotClick,
                onImportMicrosoftDynamicsClick,
                hasCrmSyncFeature,
                hasMicrosoftDynamicsAccountSync,
              )
            }
          />
        </StyledContentWrapperDiv>
        {isInfoBoxExpanded && (
          <AccountInfoBoxesContainer
            onImportCSVClick={onImportCSVClick}
            onImportSalesforceClick={onImportSalesforceClick}
          />
        )}
      </StyledWrapperDiv>
      {maybeRenderSalesforceDialog(
        panelId,
        isShowingImportSalesforceDialog,
        Boolean(shouldShowCrmSyncErrors),
        () => navigateToLink(buildContactsManagerAccountsUrl()),
      )}
      {maybeRenderHubspotDialog(panelId, isShowingImportHubspotDialog, Boolean(shouldShowCrmSyncErrors), () =>
        navigateToLink(buildContactsManagerAccountsUrl()),
      )}
    </>
  );
};

/*
 * Helpers.
 */

function maybeRenderSalesforceDialog(
  panelId: ContactsManagerPanelId,
  shouldRender: boolean,
  shouldStartOnErrorsPage: boolean,
  onRequestClose: Handler,
) {
  if (!shouldRender) {
    return null;
  }
  return (
    <SalesforceCrmSyncDialogContainer
      panelId={panelId}
      shouldStartOnErrorsPage={shouldStartOnErrorsPage}
      onRequestClose={onRequestClose}
    />
  );
}

function maybeRenderHubspotDialog(
  panelId: ContactsManagerPanelId,
  shouldRender: boolean,
  shouldStartOnErrorsPage: boolean,
  onRequestClose: Handler,
) {
  if (!shouldRender) {
    return null;
  }
  return (
    <HubspotCrmSyncDialogContainer
      panelId={panelId}
      onRequestClose={onRequestClose}
      shouldStartOnErrorsPage={shouldStartOnErrorsPage}
    />
  );
}

function renderCreateAccountButton(openCreateAccountForm: Handler) {
  return (
    <Button onClick={openCreateAccountForm}>
      <StyledButtonContentDiv>
        <StyledAccountIcon name="newAccountSmall" size={16} />
        <Trans id="contacts-manager-accounts-create">Create</Trans>
      </StyledButtonContentDiv>
    </Button>
  );
}

function maybeRenderContextDetails(
  panelId: ContactsManagerPanelId,
  totalResults: number | undefined,
  activeSyncJobs: ReadonlyArray<SyncJobModel>,
) {
  if (isUndefined(totalResults)) {
    return null;
  }

  return (
    <StyledContextDetailsWrapperDiv>
      <StyledHeaderCount>
        <Plural
          id="contacts-manager-accounts-count"
          value={totalResults}
          one={`${totalResults.toLocaleString()} account`}
          other={`${totalResults.toLocaleString()} accounts`}
        />
      </StyledHeaderCount>
      <CrmSyncHeaderDetailsContainer
        resourceType={CrmSyncIntegrationResourceTypesEnum.ACCOUNT}
        panelId={panelId}
        activeSyncJobs={activeSyncJobs}
      />
    </StyledContextDetailsWrapperDiv>
  );
}

function renderImportButton(
  onImportCSVClick: Handler,
  onImportSalesforceClick: Handler,
  onImportHubspotClick: Handler,
  onImportMicrosoftDynamicsClick: Handler,
  hasCrmSyncFeature: boolean,
  hasMicrosoftDynamicsAccountSync: boolean,
  buttonVariant?: ButtonVariantsEnum,
) {
  return (
    <ContactsManagerImportPopover
      onImportCSVClick={onImportCSVClick}
      onImportSalesforceClick={onImportSalesforceClick}
      hasCrmSyncFeature={hasCrmSyncFeature}
      onImportHubspotClick={onImportHubspotClick}
      onImportMicrosoftDynamicsClick={
        hasMicrosoftDynamicsAccountSync ? onImportMicrosoftDynamicsClick : undefined
      }
      buttonVariant={buttonVariant}
    />
  );
}
